import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ChatbotUIContext } from '@/context/context'
import { updateProfile } from '@/db/profile'
import { updateWorkspace } from '@/db/workspaces'
import { LLM, LLMID } from '@/types'
import { HoverCardPortal } from '@radix-ui/react-hover-card'
import { IconFilterOff, IconSettings } from '@tabler/icons-react'
import debounce from 'lodash/debounce'
import { VList } from 'virtua'
import { useTranslation } from 'react-i18next'

import { DEFAULT_SYSTEM_PROMPT, validateSystemPromptTemplate } from '@/lib/build-prompt'
import { CATEGORIES } from '@/lib/models/categories'
import { Button } from '@/components/ui/button'
import { AdvancedContent, InfoIconTooltip } from '@/components/ui/chat-settings-form'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { SearchInput } from '@/components/ui/search-input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { TextareaAutosize } from '@/components/ui/textarea-autosize'
import { WithTooltip } from '@/components/ui/with-tooltip'
import { ModelDetails } from '@/components/models/model-details'
import { ModelVisibilityOption } from '@/components/models/model-visibility-option'

import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card'

export const DEFAULT_MODEL_VISIBILITY: Record<LLMID, boolean> = {
  // Losi
  'losi-omega': true,
  'losi-mini': true,
  'losi-flash': true,

  // OpenAI — current GPT-5.6; hide legacy 4o/4.1/5.x/o1
  'gpt-5.6-sol': true,
  'gpt-5.6-terra': true,
  'gpt-5.6-luna': true,
  'gpt-5.5': true,
  'gpt-5.4': true,
  'gpt-5.4-mini': false,
  'gpt-5.4-pro': false,
  'gpt-5.2': false,
  'gpt-5.2-chat-latest': false,
  'gpt-5.2-pro': false,
  'gpt-5.1': false,
  'gpt-5.1-codex-max': false,
  'gpt-5': false,
  'gpt-5-mini': false,
  'gpt-5-nano': false,
  'gpt-4.1': false,
  'gpt-4.1-mini': false,
  'gpt-4.1-nano': false,
  'gpt-4o-mini': false,
  'gpt-4o-2024-11-20': false,
  'o1-2024-12-17': false,
  'o1-mini': false,
  'o3-mini': false,
  'gpt-realtime': false,
  'gpt-realtime-2': false,
  'gpt-realtime-mini-2025-12-15': false,
  'gpt-4o-realtime-preview': false,

  // Anthropic — current Fable 5.1 / Opus 5 / Sonnet 5 / Haiku; hide legacy
  'claude-fable-5-1': true,
  'claude-opus-5': true,
  'claude-sonnet-5': true,
  'claude-haiku-4-5': true,
  'claude-fable-5': false,
  'claude-opus-4-8': false,
  'claude-opus-4-6': false,
  'claude-opus-4-5': false,
  'claude-sonnet-4-5-20250929': false,
  'claude-3-opus-20240229': false,

  // Google — current 3.x / 2.5; hide shut-down 1.x / 2.0
  'gemini-3.8-flash': true,
  'gemini-3.5-flash': true,
  'gemini-3.1-pro-preview': true,
  'gemini-3-pro-image-preview': true,
  'gemini-2.5-pro': true,
  'gemini-2.5-flash': true,
  'gemma-3-27b-it': false,
  'gemini-3-pro-preview': false,
  'gemini-2.0-flash-exp': false,
  'gemini-2.0-flash-thinking-exp-1219': false,
  'gemini-1.5-pro-latest': false,
  'gemini-1.5-flash-latest': false,
  'gemini-pro': false,

  // Mistral — latest aliases only
  'mistral-large-latest': true,
  'mistral-medium-latest': true,
  'mistral-small-latest': true,
  'mistral-tiny': false,
  'mistral-small': false,
  'mistral-medium': false,

  // Groq — current OSS / Qwen; hide deprecated Llama/Mixtral
  'openai/gpt-oss-120b': true,
  'openai/gpt-oss-20b': true,
  'qwen/qwen3.6-27b': true,
  'mixtral-8x7b-32768': false,
  'llama3-70b-8192': false,
  'llama3-8b-8192': false,

  // Perplexity — hide deprecated sonar-reasoning
  sonar: true,
  'sonar-pro': true,
  'sonar-reasoning-pro': true,
  'sonar-deep-research': true,
  'sonar-reasoning': false,

  // AWS Bedrock Mantle / Nova
  'xai.grok-4.3': true,
  'global.moonshotai.kimi-k3': true,
  'qwen.qwen3-vl-235b-a22b': true,
  'qwen.qwen3-coder-next': true,
  'qwen.qwen3-next-80b-a3b': true,
  'minimax.minimax-m2.5': true,
  'minimax.minimax-m2.1': false,
  'openai.gpt-oss-120b-1:0': true,
  'zai.glm-4.7': true,
  'zai.glm-4.7-flash': true,
  'deepseek.v3.2': true,
  'moonshotai.kimi-k2.5': true,
  'amazon.nova-pro-v1:0': true,
  'amazon.nova-2-sonic-v1:0': false
}

export const ModelSettings = ({ models }: { models?: LLM[] }) => {
  const { profile, selectedWorkspace, setChatSettings, setProfile, chatSettings } =
    useContext(ChatbotUIContext)
  const { t } = useTranslation()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [reflectMode, setReflectMode] = useState(false)

  const [visibility, setVisibility] = useState<Record<LLMID, boolean>>({} as Record<LLMID, boolean>)

  const [systemPromptTemplate, setSystemPromptTemplate] = useState(
    profile?.system_prompt_template || DEFAULT_SYSTEM_PROMPT
  )

  const validSystemPrompt = validateSystemPromptTemplate(systemPromptTemplate)

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTier, setSelectedTier] = useState<string>('all')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [visibilityFilter, setVisibilityFilter] = useState<string>('all')

  const tiers = [
    { value: 'all', label: t('All Tiers') },
    { value: 'free', label: t('Free') },
    { value: 'pro', label: t('Pro') },
    { value: 'ultimate', label: t('Ultimate') }
  ]

  const categories = [
    { value: 'all', label: t('All Categories') },
    ...Object.entries(CATEGORIES).map(([key, value]) => ({
      value: key,
      label: typeof value.category === 'function' ? value.category() : value.category
    }))
  ]

  const visibilityOptions = [
    { value: 'all', label: t('All Models') },
    { value: 'enabled', label: t('Selected Models') },
    { value: 'disabled', label: t('Unselected Models') }
  ]

  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')

  // Debounce the search query update
  const debouncedSetSearchQuery = useCallback(
    debounce((value: string) => {
      setDebouncedSearchQuery(value)
    }, 300),
    []
  )

  // Update both the immediate and debounced search queries
  const handleSearchQueryChange = (value: string) => {
    setSearchQuery(value)
    debouncedSetSearchQuery(value)
  }

  // Memoize the filtered models
  const filteredModels = useMemo(() => {
    return models?.filter(
      (model) =>
        model.modelName.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) &&
        (selectedTier === 'all' || model.tier === selectedTier) &&
        (selectedCategory === 'all' ||
          model.categories?.some(
            (cat) => cat.category === CATEGORIES[selectedCategory].category
          )) &&
        (visibilityFilter === 'all' ||
          (visibilityFilter === 'enabled' && visibility[model.modelId]) ||
          (visibilityFilter === 'disabled' && !visibility[model.modelId]))
    )
  }, [models, debouncedSearchQuery, selectedTier, selectedCategory, visibilityFilter, visibility])

  useEffect(() => {
    if (models) {
      const initialVisibility = models.reduce(
        (acc, model) => {
          acc[model.modelId] =
            (profile?.model_visibility as Record<LLMID, boolean>)?.[model.modelId] ??
            DEFAULT_MODEL_VISIBILITY[model.modelId] ??
            false
          return acc
        },
        {} as Record<LLMID, boolean>
      )
      setVisibility(initialVisibility)
    }
  }, [models, profile])

  function handleSave() {
    if (!profile) {
      return
    }
    setProfile({
      ...profile,
      model_visibility: visibility,
      system_prompt_template: systemPromptTemplate
    })
    updateProfile(profile.id, {
      ...profile,
      model_visibility: visibility,
      system_prompt_template: systemPromptTemplate
      // reflectMode: chatSettings?.reflectMode
    })
    updateWorkspace(selectedWorkspace!.id, {
      // ...selectedWorkspace!,
      default_temperature: chatSettings?.temperature,
      default_context_length: chatSettings?.contextLength
    })
    setDialogOpen(false)
  }

  const resetFilters = () => {
    setSearchQuery('')
    setSelectedTier('all')
    setSelectedCategory('all')
    setVisibilityFilter('all')
  }

  const SYSTEM_PROMPT_DESCRIPTION = t(
    "The system prompt is a message that the AI will use to start the conversation. It should contain the following dynamic variables for Losi Chat functioning properly: {profile_context}, {local_date}, and {assistant}. {profile_context} is the user's profile context, {local_date} is the current date, and {assistant} is the name of the assistant and it's instructions."
  )

  const SYSTEM_PROMPT_WARNING = t(
    "The system prompt should contain the following dynamic variables for Losi Chat functioning properly: {profile_context}, {local_date}, and {assistant}. {profile_context} is the user's profile context, {local_date} is the current date, and {assistant} is the name of the assistant and it's instructions."
  )

  const REFLECT_WARNING = t(
    'Losi can take time to think deeply, reflect on thoughts and provide improved responses.'
  )

  return (
    <div id="model-settings" className="flex flex-col space-y-4">
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogTrigger asChild>
          <div
            className={
              'hover:bg-accent flex w-full cursor-pointer items-center justify-start truncate rounded p-2 text-sm'
            }
          >
            <IconSettings stroke={1.5} className={'mr-2 shrink-0 opacity-50'} size={24} />{' '}
            <div className={'flex flex-col'}>
              <div className={'flex items-center space-x-3'}>{t('Discover and manage models')}</div>
              <div className={'text-foreground/60 text-xs'}>
                {t('Configure and discover all LLM models here.')}
              </div>
            </div>
          </div>
        </DialogTrigger>
        <DialogContent className="flex h-[90vh] max-w-2xl flex-col">
          <DialogTitle>{t('Manage models')}</DialogTitle>
          <DialogDescription>{t('Configure and discover all LLM models here.')}</DialogDescription>
          <Tabs className="flex w-full grow flex-col">
            <TabsList className="mx-auto">
              <TabsTrigger value="visibility" title={t('Model visibility')}>
                {t('Discover models')}
              </TabsTrigger>
              <TabsTrigger value="basic" title={t('Basic settings')}>
                {t('Model parameters')}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="basic" className="flex grow flex-col [&[hidden]]:hidden">
              <div className="mb-4 mt-2 flex items-center space-x-2">
                <Label>{t('System Prompt')}</Label>
                <InfoIconTooltip label={SYSTEM_PROMPT_DESCRIPTION} />
              </div>
              <TextareaAutosize
                minRows={3}
                className="mt-2 border"
                value={systemPromptTemplate}
                onValueChange={(value) => {
                  setSystemPromptTemplate(value)
                }}
              />
              <div className="flex items-center space-x-2">
                <Button
                  onClick={() => {
                    setSystemPromptTemplate(DEFAULT_SYSTEM_PROMPT)
                  }}
                  className={'text-xs'}
                  variant={'link'}
                >
                  {t('Reset to default')}
                </Button>
                {!validSystemPrompt && (
                  <WithTooltip
                    trigger={
                      <div className="text-xs text-yellow-500">
                        {t('Missing dynamic variables')}
                      </div>
                    }
                    display={SYSTEM_PROMPT_WARNING}
                  />
                )}
              </div>
              <AdvancedContent
                showOverrideSystemPrompt={true}
                chatSettings={chatSettings!}
                onChangeChatSettings={setChatSettings}
                showTooltip={true}
              />
            </TabsContent>
            <TabsContent value="visibility" className="flex grow flex-col justify-start">
              <div className="mb-4 flex flex-col flex-wrap items-center gap-2">
                <SearchInput
                  placeholder={t('Search models...')}
                  value={searchQuery}
                  onChange={handleSearchQueryChange}
                  className="w-full"
                />
                <div className="grid w-full grid-cols-3 gap-2">
                  <Select value={selectedTier} onValueChange={(value) => setSelectedTier(value)}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('Select tier')} />
                    </SelectTrigger>
                    <SelectContent>
                      {tiers.map((tier) => (
                        <SelectItem key={tier.value} value={tier.value}>
                          {tier.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={selectedCategory}
                    onValueChange={(value) => setSelectedCategory(value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('Select category')} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category.value} value={category.value}>
                          {category.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={visibilityFilter}
                    onValueChange={(value) => setVisibilityFilter(value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('Filter visibility')} />
                    </SelectTrigger>
                    <SelectContent>
                      {visibilityOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="text-muted-foreground mb-2 text-sm">
                {t(
                  'Note: Not all models have assigned categories. Filtering by category may exclude some models.'
                )}
              </div>
              <div className="grow overflow-hidden">
                <VList className="size-full space-y-0">
                  {filteredModels && filteredModels.length > 0 ? (
                    filteredModels.map((model) => (
                      <HoverCard key={model.modelId} openDelay={100} closeDelay={0}>
                        <HoverCardTrigger>
                          <ModelVisibilityOption
                            selected={visibility[model.modelId] ?? false}
                            model={model}
                            onSelect={(checked) => {
                              setVisibility((prev) => ({
                                ...prev,
                                [model.modelId]: checked
                              }))
                            }}
                          />
                        </HoverCardTrigger>
                        <HoverCardPortal>
                          <HoverCardContent
                            align="start"
                            sideOffset={-4}
                            side="left"
                            className="w-90"
                          >
                            <ModelDetails model={model} />
                          </HoverCardContent>
                        </HoverCardPortal>
                      </HoverCard>
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center py-4">
                      <p className="text-muted-foreground mb-2">
                        {t('No models found with the current filters.')}
                      </p>
                      <Button
                        onClick={resetFilters}
                        variant="outline"
                        className="flex items-center"
                      >
                        <IconFilterOff className="mr-2" size={16} />
                        {t('Reset Filters')}
                      </Button>
                    </div>
                  )}
                </VList>
              </div>
            </TabsContent>

            {/* <div className="mb-4 mt-8 flex items-center space-x-6">
              <div className={"flex items-center space-x-2 text-nowrap"}>
                <Label>Let Losi think deeply</Label>
                <WithTooltip
                  trigger={
                    <IconInfoCircle
                      className="cursor-hover:bg-accent"
                      size={16}
                    />
                  }
                  display={REFLECT_WARNING}
                />
              </div>
              <Switch
                checked={chatSettings?.reflectMode || false}
                onCheckedChange={checked =>
                  setChatSettings(prevSettings => ({
                    ...prevSettings,
                    reflectMode: checked
                  }))
                }
              />
            </div> */}
          </Tabs>
          <Button onClick={handleSave}>{t('Save for all future chats')}</Button>
        </DialogContent>
      </Dialog>
    </div>
  )
}
